import { create } from 'zustand'
import type { Session, User } from '@supabase/supabase-js'
import { supabase, supabaseConfigured } from '@/lib/supabase'
import { catchUp, drainOutbox, subscribeFamily } from '@/lib/sync'
import { stableDeviceId } from '@/lib/device'
import { useToasts } from '@/lib/toast'
import { pushSummaryForEvent } from '@/lib/pushSummary'
import { useUnread, tabsForEvent } from '@/lib/unreadBadges'
import { setClientErrorSinkFamily } from '@/lib/clientErrorSink'
import { setAnalyticsFamily } from '@/lib/analytics'
import { getQueryClient } from '@/main'
import type { EventRow, EventType, JSONValue, Person, RoleInFamily } from '@/lib/types'

export type SessionStatus = 'loading' | 'unconfigured' | 'signed_out' | 'signed_in_no_family' | 'active'

interface AppStoreState {
  status: SessionStatus
  session: Session | null
  user: User | null
  familyId: string | null
  familyRole: RoleInFamily | null
  persons: Person[]
  focusedPersonId: string | null
  openNeedsCount: number
  online: boolean
  sessionExpiredNotice: boolean
  consentVersion: number | null      // user's stored consent_version (null = never consented)
  needsConsent: boolean               // true when consentVersion < CONSENT_VERSION constant

  // actions
  init: () => Promise<void>
  refreshSession: () => Promise<void>
  refreshFamilyContext: () => Promise<void>
  setFocusedPerson: (personId: string) => void
  signOut: () => Promise<void>
  clearSessionExpiredNotice: () => void
  markConsented: (version: number) => void

  // internal cleanup
  cleanupRealtime: (() => Promise<void>) | null
}

export const useAppStore = create<AppStoreState>((set, get) => ({
  status: 'loading',
  session: null,
  user: null,
  familyId: null,
  familyRole: null,
  persons: [],
  focusedPersonId: null,
  openNeedsCount: 0,
  online: typeof navigator === 'undefined' ? true : navigator.onLine,
  sessionExpiredNotice: false,
  consentVersion: null,
  needsConsent: false,
  cleanupRealtime: null,

  init: async () => {
    if (!supabaseConfigured()) {
      set({ status: 'unconfigured' })
      return
    }

    // Idempotent guard: React.StrictMode double-invokes useEffect in dev mode.
    // Without this, every render mount fires refreshSession + refreshFamilyContext twice,
    // creating duplicate in-flight requests that get aborted on the next navigation.
    const w = globalThis as unknown as { __babyos_inited?: boolean }
    if (w.__babyos_inited) return
    w.__babyos_inited = true

    if (typeof window !== 'undefined') {
      window.addEventListener('online', () => {
        set({ online: true })
        void drainOutbox()
        const fid = get().familyId
        if (fid) void catchUp(fid)
      })
      window.addEventListener('offline', () => set({ online: false }))
      // Refresh when tab becomes visible again — cheap catch-up.
      // Also: when the tab HIDES, tear down the realtime channels so we don't burn
      // battery on background websockets. When it becomes visible again we re-init
      // via refreshSession which re-subscribes.
      document.addEventListener('visibilitychange', () => {
        const fid = get().familyId
        if (document.visibilityState === 'visible') {
          if (fid) {
            void catchUp(fid)
            // Re-establish realtime if we tore it down
            if (!get().cleanupRealtime) void get().refreshSession()
          }
        } else {
          // Hidden — release realtime channels
          const prev = get().cleanupRealtime
          if (prev) {
            void prev().then(() => set({ cleanupRealtime: null }))
          }
        }
      })
    }

    supabase.auth.onAuthStateChange(async (evt, session) => {
      const prevStatus = get().status
      set({ session, user: session?.user ?? null })
      await get().refreshSession()

      // Show a friendly "session expired" note if the user WAS active and just got signed out
      // by supabase (refresh-token expired, revoked, etc.). Distinct from explicit signOut,
      // which sets the notice off.
      if (evt === 'SIGNED_OUT' && (prevStatus === 'active' || prevStatus === 'signed_in_no_family')) {
        set({ sessionExpiredNotice: true })
      }

      // Lifecycle telemetry — fires to Dan's oakden-hub webhook when configured.
      if (evt === 'SIGNED_IN') {
        void import('@/lib/errorReporter').then((m) => m.reportInfo('user_signed_in', { email: session?.user?.email }))
      }
      if (evt === 'PASSWORD_RECOVERY') {
        void import('@/lib/errorReporter').then((m) => m.reportInfo('password_recovery_started'))
      }

      // Belt-and-suspenders for the invite flow: if the user just signed in AND there's a
      // pending invite token in sessionStorage (set by JoinScreen), navigate to /join so the
      // accept flow runs — even if the email-confirm redirect took them to `/` instead.
      if (evt === 'SIGNED_IN' && typeof window !== 'undefined') {
        try {
          const pending = sessionStorage.getItem('babyos.pending_invite')
          if (pending && window.location.pathname !== '/join') {
            window.history.replaceState(null, '', `/join?token=${encodeURIComponent(pending)}`)
            window.dispatchEvent(new PopStateEvent('popstate'))
          }
        } catch { /* noop */ }
      }
    })

    await get().refreshSession()
  },

  refreshSession: async () => {
    const { data } = await supabase.auth.getSession()
    set({ session: data.session, user: data.session?.user ?? null })
    if (!data.session) {
      set({ status: 'signed_out', familyId: null, persons: [], focusedPersonId: null })
      setClientErrorSinkFamily(null)
      void setAnalyticsFamily(null)
      const prev = get().cleanupRealtime
      if (prev) await prev()
      set({ cleanupRealtime: null })
      return
    }

    // Read the user's stored consent version. Null / stale means we need to prompt.
    // Ignore any error — if the row doesn't exist yet (fresh sign-up before family bootstrap),
    // needsConsent stays true and the modal will render post-bootstrap.
    const { data: userRow } = await supabase
      .from('users')
      .select('consent_version')
      .eq('id', data.session.user.id)
      .maybeSingle()
    const consentVersion = (userRow?.consent_version as number | null) ?? null
    // CONSENT_VERSION is imported by the modal — mirror it here so we don't circular-import.
    // Bump both together when the notice materially changes.
    const CURRENT_CONSENT_VERSION = 1
    const needsConsent = consentVersion == null || consentVersion < CURRENT_CONSENT_VERSION
    set({ consentVersion, needsConsent })

    // Look up active family memberships
    const { data: memberships, error } = await supabase
      .from('family_members')
      .select('family_id, role, status')
      .eq('user_id', data.session.user.id)
      .eq('status', 'active')

    if (error) {
      // eslint-disable-next-line no-console
      console.error('[BabyOS] family_members query failed', error)
      set({ status: 'signed_out' })
      return
    }

    if (!memberships || memberships.length === 0) {
      set({ status: 'signed_in_no_family', familyId: null, persons: [] })
      return
    }

    const first = memberships[0] as { family_id: string; role: RoleInFamily; status: string }
    const previousFamilyId = get().familyId
    set({ familyId: first.family_id, familyRole: first.role, status: 'active' })
    setClientErrorSinkFamily(first.family_id)
    void setAnalyticsFamily(first.family_id)

    await get().refreshFamilyContext()

    // Subscribe to realtime exactly once per family. If we're already subscribed to this
    // family, leave the existing channel in place (avoids `cannot add postgres_changes
    // callbacks after subscribe()` error + the runaway refresh churn).
    if (previousFamilyId !== first.family_id) {
      const prevCleanup = get().cleanupRealtime
      if (prevCleanup) await prevCleanup()

      const deviceId = await stableDeviceId()
      const handles = subscribeFamily(first.family_id, deviceId, (row: EventRow) => {
        // Refresh persons + needs count for the top bar
        void get().refreshFamilyContext()

        // Invalidate every read query so timeline + today + ecs re-fetch immediately.
        // Keys use the ['timeline', ...] and ['ecs', ...] patterns; simplest is to
        // invalidate broadly and let React Query dedupe.
        try {
          const qc = getQueryClient()
          void qc.invalidateQueries({ queryKey: ['timeline'] })
          void qc.invalidateQueries({ queryKey: ['ecs'] })
          void qc.invalidateQueries({ queryKey: ['person'] })
          void qc.invalidateQueries({ queryKey: ['person-needs'] })
          void qc.invalidateQueries({ queryKey: ['person-growth'] })
        } catch { /* QueryClient may not be ready during boot */ }

        // Show a toast so the user KNOWS someone else logged something without
        // having to notice the number changed. Skip corrections + person lifecycle.
        const summary = pushSummaryForEvent(row.event_type as EventType, row.payload as JSONValue)
        if (summary) {
          useToasts.getState().push({
            title: summary.title,
            body: summary.body,
            tone: row.event_type.startsWith('early_care.diaper') || row.event_type.startsWith('early_care.sleep')
              ? 'mint'
              : 'coral',
            url: summary.url,
            ttl: 6000,
          })
        }

        // Bump per-tab unread badges (Timeline always, plus Today/Needs/Family per event)
        for (const tab of tabsForEvent(row.event_type)) {
          useUnread.getState().bump(tab)
        }
      })
      set({ cleanupRealtime: handles.cleanup })

      void catchUp(first.family_id)
      void drainOutbox()
    }
  },

  /**
   * Cheap re-fetch of persons + open-needs count. Does NOT touch realtime — that's owned by
   * refreshSession (subscribes once per family). Safe to call on tab focus, after every
   * outbox flush, from realtime callbacks, etc.
   */
  refreshFamilyContext: async () => {
    const familyId = get().familyId
    if (!familyId) return

    const [personsRes, needsCountRes] = await Promise.all([
      supabase.from('persons').select('*').eq('family_id', familyId).neq('identity_status', 'archived'),
      supabase
        .from('needs')
        .select('id', { count: 'exact', head: true })
        .eq('family_id', familyId)
        .in('status', ['open', 'in_progress']),
    ])

    const persons = (personsRes.data ?? []) as Person[]
    const openNeedsCount = needsCountRes.count ?? 0

    // Default focused person = youngest baby/child/unborn, else first person.
    // Preserve the current focused person if it's still in the list (don't yank focus
    // away from what the user was looking at).
    const currentFocused = get().focusedPersonId
    let focused: string | null = null
    if (currentFocused && persons.some((p) => p.id === currentFocused)) {
      focused = currentFocused
    } else {
      focused = persons
        .filter((p) => ['baby', 'child', 'unborn'].includes(p.person_type))
        .sort((a, b) => {
          const aKey = a.date_of_birth ?? a.due_date ?? '0000-01-01'
          const bKey = b.date_of_birth ?? b.due_date ?? '0000-01-01'
          return bKey.localeCompare(aKey)
        })[0]?.id ?? persons[0]?.id ?? null
    }

    set({ persons, openNeedsCount, focusedPersonId: focused })
  },

  setFocusedPerson: (personId: string) => set({ focusedPersonId: personId }),

  signOut: async () => {
    const prev = get().cleanupRealtime
    if (prev) await prev()
    await supabase.auth.signOut()
    set({
      status: 'signed_out',
      session: null,
      user: null,
      familyId: null,
      familyRole: null,
      persons: [],
      focusedPersonId: null,
      openNeedsCount: 0,
      sessionExpiredNotice: false,   // explicit sign-out = don't show "expired" note
      cleanupRealtime: null,
    })
    setClientErrorSinkFamily(null)
    void setAnalyticsFamily(null)
  },

  clearSessionExpiredNotice: () => set({ sessionExpiredNotice: false }),

  markConsented: (version: number) => set({ consentVersion: version, needsConsent: false }),
}))
